/**
 * IMPORTS
 */
import {differenceInDays} from 'date-fns';
import find from 'lodash/find';
import accountsSelectors from 'src/aggregates/accounts/selectors';
import {fetchByRole} from 'src/aggregates/agents/selectors';
import {closeSession as closeSessionAPI}
    from 'src/aggregates/conversations/api';
import {focus as focusAPI} from 'src/aggregates/conversations/api';
import {openSession as openSessionAPI} from 'src/aggregates/conversations/api';
import {onHeartbeatFailure as onHeartbeatFailureAPI}
    from 'src/aggregates/conversations/api';
import {remove as removeConversation} from 'src/aggregates/conversations/api';
import {resetTransfer as resetTransferConversation}
    from 'src/aggregates/conversations/api';
import * as events from 'src/aggregates/conversations/events';
import {getMessage} from 'src/aggregates/conversations/feedbacks';
import {CONVERSATION_ANALYZED} from 'src/aggregates/conversations/quality';
import * as qualityUtils from 'src/aggregates/conversations/quality/utils';
import * as responders from 'src/aggregates/conversations/responders';
import selectors from 'src/aggregates/conversations/selectors';
import {findSiblingConversation} from 'src/aggregates/conversations/siblings';
import {findSiblingConversationOnStore} from
    'src/aggregates/conversations/siblings';
import * as session from 'src/aggregates/conversations/session/session';
import * as utils from 'src/aggregates/conversations/utils';
import {create as createConversation} from 'src/aggregates/conversations/utils';
import {fetch as fetchConversation} from 'src/aggregates/conversations/utils';
import {fetchMessages} from 'src/aggregates/conversations/utils';
import {fetchPage} from 'src/aggregates/conversations/utils';
import {fetchRealTimeMetrics as getRealTimeMetrics}
    from 'src/aggregates/conversations/utils';
import {finish as finishConversation} from 'src/aggregates/conversations/utils';
import {forceFinish as forceFinishConversation}
    from 'src/aggregates/conversations/utils';
import {getOnlineAgents} from 'src/aggregates/conversations/utils';
import {load as loadConversation} from 'src/aggregates/conversations/utils';
import {loadByPeriod as loadConversationsByPeriod}
    from 'src/aggregates/conversations/utils';
import {loadConversationCustomer} from 'src/aggregates/conversations/utils';
import {messageTypesIdToName} from 'src/aggregates/conversations/utils';
import {messageTypesNameToId} from 'src/aggregates/conversations/utils';
import {moveToHistory} from 'src/aggregates/conversations/utils';
import {moveToOngoing} from 'src/aggregates/conversations/utils';
import {moveToTop} from 'src/aggregates/conversations/utils';
import {removeId} from 'src/aggregates/conversations/utils';
import {removePreviousConversations} from 'src/aggregates/conversations/utils';
import {sendAutoAnswer} from 'src/aggregates/conversations/utils';
import {sendMessage as send} from 'src/aggregates/conversations/utils';
import {start as startConversations} from 'src/aggregates/conversations/utils';
import {uploadEmail} from 'src/aggregates/conversations/utils';
import {uploadFile} from 'src/aggregates/conversations/utils';
import {retryMessage as retry} from 'src/aggregates/conversations/utils';
import customers from 'src/aggregates/customers/selectors';
import {contactTypesChannelToId} from 'src/aggregates/customers/utils';
import {ensureCustomer} from 'src/aggregates/customers/utils';
import {getMappedContactUids} from 'src/aggregates/customers/utils';
import {mergeMappedContacts} from 'src/aggregates/customers/utils';
import {add as addTimeline} from 'src/aggregates/timelines/api';
import {clearByConversation} from 'src/aggregates/timelines/api';
import {fetchMessageByOriginalId as fetchTimelineMessageByOriginalId}
    from 'src/aggregates/timelines/api';
import {remove as removeTimeline} from 'src/aggregates/timelines/api';
import {logger} from 'src/logger';
import {getConversation as getTimeline}
    from 'src/aggregates/timelines/selectors';
import {getContactTimeline} from 'src/aggregates/timelines/utils';
import {fetch as fetchUser} from 'src/aggregates/user/selectors';
import {reduceContact} from 'src/aggregates/utils/contactcommunicationinfo';
import {reducePayload} from 'src/aggregates/utils/payloads';
import Metadata from 'src/config/Metadata';
import routerHistory from 'src/history';
import Store from 'src/store';
import {focus as focusScreen} from 'src/utils/focus';
import {getReducedData} from 'src/utils/getreduceddata';
import {showUnavailableConversationPopup} from 'src/utils/popup';
import uuid4 from 'uuid4';


/**
 * TYPES
 */
import {Role} from 'src/aggregates/agents/state.d';
import * as Commands from 'src/aggregates/conversations/commands.d';
import {IConversationOnUploadProgress}
    from 'src/aggregates/conversations/commands.d';
import {IConversationOnUploadSuccess}
    from 'src/aggregates/conversations/commands.d';
import {IConversationsByPeriod} from 'src/aggregates/conversations/events.d';
import {MessageTypes} from 'src/aggregates/conversations/message/messages.d';
import {IConversationsReportMetric}
    from 'src/aggregates/conversations/queries.d';
import {IOngoingConversation} from 'src/aggregates/conversations/queries.d';
import {IChatState} from 'src/aggregates/conversations/state.d';
import {IConversation} from 'src/aggregates/conversations/state.d';
import {IConversationById} from 'src/aggregates/conversations/state.d';
import {IConversationHistory} from 'src/aggregates/conversations/state.d';
import {IMessage} from 'src/aggregates/conversations/state.d';
import {IMetrics} from 'src/aggregates/conversations/state.d';
import {IQualityConversation} from 'src/aggregates/conversations/state.d';
import {IMessagesMetricsByAgent} from 'src/aggregates/conversations/utils.d';
import {IAppState} from 'src/aggregates/index.d';
import {IEvent} from 'src/aggregates/index.d';
import {IMessage as ITimelineMessage} from 'src/aggregates/timelines/state.d';


/**
 * CONSTANTS AND DEFINITIONS
 */
const DURATION = 100;
const TRIES = 3;
const PAGE_SIZE = 30;

// reconnections counter
let count = 0;


/**
 * CODE
 */

/**
 * I am a conversation add forwarding message command handler.
 *
 * :param command: conversation add forwarding message command
 * :param state: application state
 *
 * :generates: events
 */
async function* addForwardingMessage (
    command: Commands.IConversationAddForwardingMessage,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate forwarding message added event
    yield events.conversationForwardingMessageAdded(command.message);
}


/**
 * I am close session command handler.
 *
 * :generates: events
 */
async function* closeSession (
    command: Commands.IConversationCloseSession,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // close session on conversis
    await session.close(state.user.id, state.conversation.user.session);

    // generate logout event
    yield events.conversationSessionClosed();
}


/**
 * I am a conversation listen command handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :generates: events
 */
async function* listen (
    command: Commands.IConversationListen,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // register conversations endpoints
    responders.start();
}


/**
 * I am a conversation exited command handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :generates: events
 */
async function* onExit (
    command: Commands.IConversationOnExit,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get conversation from store
    const conversation = state.conversation.byId[command._target];

    // conversation does not exist: stop
    if (conversation === undefined)
    {
        return;
    }

    // conversation should not exit: update it and abort
    if (conversation.shouldExit === false)
    {
        yield events.conversationShouldExit(command._target);
        return;
    }

    // conversation should exit: move it to timeline
    await addTimeline(
        conversation.account,
        command.uid,
        customers.getContactByChannelAndUid(
            contactTypesChannelToId.get(conversation.channel),
            conversation.remote,
        ).id,
        command.end,
        conversation.id,
        command.lastInteraction,
        Object.values(conversation.messages),
        conversation.remote,
        conversation.start,
        command.status,
        conversation.subject,
    );

    // update history and ongoing
    const updatedState = Store.getState();
    const {history, ongoing} = moveToHistory(conversation.channel,
                                             conversation.remote,
                                             updatedState.conversation.byId,
                                             updatedState.conversation.history,
                                             updatedState.conversation.ongoing);

    // generate conversationRemoved event;
    yield events.conversationRemoved(command._target, history, ongoing);

    // get the customer of the exited conversation contact
    const holder = customers.getCustomerByContact(
        contactTypesChannelToId.get(conversation.channel),
        conversation.remote,
    );

    // customer is not on the store: return
    if (holder === null)
    {
        return;
    }

    // merge the card of the exited bsuid into the customer that holds it
    const kept = await mergeMappedContacts(holder.id);

    // get the customer whose page is on screen after the merge
    const [view, focused] = routerHistory.location.pathname
        .replace(/^\//, '').split('/');

    // customer on screen was not merged into another one: return
    if (kept === holder.id || view !== 'dashboard' ||
        focused !== String(holder.id))
    {
        return;
    }

    // reduce the kept customer and focus it
    const reduced = getReducedData(kept);
    await focusScreen(reduced.account,
                      reduced.contactId,
                      reduced.conversationId,
                      kept);

    // redirect to the kept customer page
    routerHistory.push(
        `/dashboard/${kept}/${reduced.channel}/${reduced.typeId}`);
}


/**
 * I am a conversations load command handler.
 *
 * :param command: command
 * :param state: state
 *
 * :generates: events
 */
async function* load (
    command: Commands.IConversationLoad,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate loading conversations event
    yield events.conversationLoading();

    // load conversations
    const conversations = await loadConversation(command.agent,
                                                 state.user.company);

    // conversations load failed: generate event and return
    if (conversations === null)
    {
        yield events.conversationNotLoaded();
        return;
    }

    // make a copy of conversations to avoid read only error
    const userConversations = [...conversations];

    // conversations loaded: sort conversations by lastInteraction descendengly
    userConversations.sort(
        (a: IOngoingConversation, b: IOngoingConversation): number => (
            b.lastInteraction - a.lastInteraction
        ),
    );

    // add unassociated customer for needed conversations, check if current
    // conversation is still with user and start ongong
    const ongoingCustomers: (number | string)[] = [];
    const ongoingConversations: IOngoingConversation[] = [];
    for (const conversation of userConversations)
    {
        // current conversation has agent answer is undefined: set it to null
        if (conversation?.hasAgentAnswer === undefined)
        {
            conversation.hasAgentAnswer = null;
        }

        // load customer's conversation and get its id
        const customerId = await loadConversationCustomer(conversation);

        // check if customer already exists in the ongoing conversations
        const index = ongoingCustomers.findIndex(
            element => element === customerId,
        );

        // customer not in the ongoing conversations: add it
        if (index === -1)
        {
            ongoingCustomers.push(customerId);
        }

        // add conversation with proper customer id
        ongoingConversations.push({...conversation, customer: customerId});
    }

    // generate converstions loaded event
    yield events.conversationLoaded(
        ongoingConversations,
        ongoingCustomers,
    );
}


/**
 * I am a conversations load by period command handler.
 *
 * :param command: load page command
 * :param state: state
 *
 * :generates: events
 */
async function* loadByCustomer (
    command: Commands.IConversationLoadByCustomer,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate loading conversations event
    yield events.conversationLoadingByCustomer(command.customer);

    // fetch customer contacts
    const contacts = customers.fetch(command.customer)?.contacts;

    // contacts not loaded: generate error event and return
    if (contacts === undefined)
    {
        yield events.conversationNotLoadedByCustomer(command.customer);
        return;
    }

    // initialize conversations history
    const history = [] as IConversationHistory[];

    // initialize new conversation ids array
    const finishedIds: IConversation['id'][] = [];
    const ongoingIds: IConversation['id'][] = [];

    // fetch conversations for each contact
    for (const contact of contacts)
    {
        // retrieve contact communication information
        const contactCommunication = reduceContact(contact);

        // iterate over each contact communication and retrieve conversations
        for (const information of contactCommunication)
        {
            // list conversations with current communication information
            const conversations = await getContactTimeline(information.account,
                                                           state.user.company,
                                                           information.uid);

            // conversations is null: fail loading
            if (conversations === null)
            {
                yield events.conversationNotLoadedByCustomer(command.customer);
                return;
            }

            // set conversations data
            for (const conversation of conversations)
            {
                // conversations is ongoing: push id, save in byId and continue
                if (conversation.end === undefined || conversation.end === null)
                {
                    ongoingIds.push(conversation.id);
                    yield events.conversationAdded(
                        conversation.account,
                        '',
                        null,
                        null,
                        [],
                        conversation.id,
                        false,
                        conversation.lastInteraction,
                        {},
                        [],
                        '',
                        conversation.start,
                        conversation.subject,
                        false,
                        null,
                        conversation.unansweredMessages,
                        command.customer,
                    );
                    continue;
                }

                // push id
                finishedIds.push(conversation.id);

                // get conversation from state
                const stored = selectors.getFinished(conversation.id);

                // push data
                history.push({
                    ...stored,
                    account: information.account,
                    agentFinished: conversation.agentFinished,
                    channel: information.channel,
                    end: conversation.end,
                    id: conversation.id,
                    lastInteraction: conversation.lastInteraction,
                    remote: information.uid,
                    start: conversation.start,
                    subject: conversation.subject,
                    templateRequired: conversation.templateRequired,
                    templateSent: conversation.templateSent,
                    unansweredMessages: conversation.unansweredMessages,
                });
            }
        }
    }

    // has finished conversations: save in finishedById and in customer finished
    if (finishedIds.length > 0)
    {
        yield events.conversationHistoryAdded(history);
        yield events.conversationLoadedByCustomer(
            command.customer,
            finishedIds,
        );
    }

    // has ongoing conversations: save in customer ongoing
    if (ongoingIds.length > 0)
    {
        yield events.conversationLoadedOngoingByCustomer(
            command.customer,
            ongoingIds,
        );
    }

    // no conversations: generate event to stop loading
    if (finishedIds.length === 0 && ongoingIds.length === 0)
    {
        yield events.conversationStopLoadByCustomer();
    }
}


/**
 * I am a conversations load by period command handler.
 *
 * :param command: load page command
 * :param state: state
 *
 * :generates: events
 */
async function* loadByPeriod (
    command: Commands.IConversationLoadByPeriod,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate loading conversations event
    yield events.conversationLoadingByPeriod(command.agent);

    // load conversations
    const loadedConversations = await loadConversationsByPeriod(
        command.agent,
        state.user.company,
        command.from,
        command.to,
    );

    // conversations load failed: generate event and return
    if (loadedConversations === null || loadedConversations === undefined)
    {
        yield events.conversationNotLoadedByPeriod(command.agent);
        return;
    }

    // no conversation in period: generate event and return
    if (loadedConversations.length === 0)
    {
        yield events.conversationLoadedAllByPeriod(command.agent, command.from);
        return;
    }

    // load conversations customers
    const conversations: IConversationsByPeriod[] = [];

    // conversations loaded: load customers and set end timestamp
    for (const conversation of Object.values(loadedConversations))
    {
        // load customer's conversation and get its id
        const customerId = await loadConversationCustomer(conversation);

        // set conversation end timestamp
        const end = conversation.end * 1000 || null;

        // add customer id
        conversations.push({...conversation, customerId, end});
    }

    // set default values for agent conversations and 'since'
    let since = command.from;

    // agent exists: override default values
    const agent = state.conversation.byAgent[command.agent];
    if (agent !== undefined)
    {
        since = Math.min(agent.since, since);
    }

    // get finished conversations from state
    const finishedConversations: IConversationById
        = state.conversation.finishedById;

    // filter out conversations already in state
    const newConversations = conversations.filter((conversation) =>
    {
        return finishedConversations[conversation.id] === undefined;
    });

    // get conversations ids
    const ids = conversations.map(
        (conversation) => conversation.id,
    );

    // set conversations history data
    const history = newConversations.map(conversation =>
    {
        // get conversation from state
        const stored = selectors.getFinished(conversation.id);

        // return data
        return {
            ...stored,
            account: conversation.account.id,
            channel: conversation.account.channel,
            customer: conversation.customerId,
            end: conversation.end,
            id: conversation.id,
            lastInteraction: conversation.lastInteraction,
            remote: conversation.contact.uid,
            start: conversation.start,
            subject: conversation.subject,
            unansweredMessages: conversation.unansweredMessages,
        };
    });

    // generate converstions history added event
    yield events.conversationHistoryAdded(history);

    // generate converstions loaded event
    yield events.conversationLoadedByPeriod(command.agent, ids, since);
}


/**
 * I am a load ongoing conversations by customer command handler.
 *
 * :param command: command
 * :param state: state
 *
 * :generates: events
 */
async function* loadOngoingByCustomer (
    command: Commands.IConversationLoadOngoingByCustomer,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get customer contacts
    const contacts = customers.fetch(command.customer)?.contacts;

    // contacts not found: stop
    if (contacts === undefined)
    {
        return;
    }

    // reduce customer contacts uids
    const reducedContacts = contacts.map(contact => contact.value);

    // generate loading event
    yield events.conversationLoadingOngoingByCustomer();

    // fetch conversations
    const result = await utils.fetchContactsOngoing(reducedContacts);

    // request failed: generate not loaded event and stop
    if (result === null)
    {
        yield events.conversationNotLoadedOngoingByCustomer();
        return;
    }

    // initialize conversations ids list
    const ids: number[] = [];

    // push ids to list and save all conversations on store
    for (const conversation of result)
    {
        ids.push(conversation.id);
        yield events.conversationAdded(
            conversation.account.id,
            conversation.account.channel,
            conversation.end,
            conversation.hasAgentAnswer,
            [],
            conversation.id,
            false,
            conversation.lastInteraction,
            {},
            [],
            conversation.contact.uid,
            conversation.start,
            conversation.subject,
            false,
            null,
            conversation.unansweredMessages,
            command.customer,
        );
    }

    // generate event to save conversations ids in customer
    yield events.conversationLoadedOngoingByCustomer(command.customer, ids);
}


/**
 * I am a conversations load page command handler.
 *
 * :param command: load page command
 * :param state: state
 *
 * :generates: events
 */
async function* loadPage (
    command: Commands.IConversationLoadPage,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate page loading event
    yield events.conversationPageLoading();

    // get history
    const history: (number | string)[] = [...state.conversation.history];
    const length = history.length;

    // get page
    let pageNumber = state.conversation.page;

    // load conversations page
    while (length === history.length &&
           pageNumber < state.conversation.page + 10)
    {
        // get agent company and session
        const {company} = fetchUser();
        const session = selectors.getSession();

        // fetch page and increment page number
        const page = await fetchPage(company,
                                     pageNumber,
                                     session,
                                     PAGE_SIZE);
        pageNumber += 1;

        // conversations load page failed: generate event and return
        if (page === null)
        {
            yield events.conversationPageNotLoaded();
            return;
        }

        // all pages loaded: generate event and return
        if (page.length === 0)
        {
            yield events.conversationAllPagesLoaded();
            return;
        }

        // page loaded: get customers to be added to history
        for (const conversation of page)
        {
            // load customer's conversation and get its id
            const customerId = await loadConversationCustomer(conversation);

            // get customer index on history
            const historyIndex = history.findIndex(
                (element: number | string): boolean => element === customerId,
            );

            // get customer index on ongoing
            const ongoingIndex = state.conversation.ongoing.findIndex(
                (element: number | string): boolean => element === customerId,
            );

            // customer does not exist on history nor ongoing: add id to history
            if (historyIndex === -1 && ongoingIndex === -1)
            {
                history.push(customerId);
            }
        }
    }

    // generate page loaded event
    yield events.conversationPageLoaded(history, pageNumber);
}


/**
 * I am a conversations prioritize order command handler.
 *
 * :param command: prioritize order command
 * :param state: state
 *
 * :generates: events
 */
async function* prioritizeOrder (
    command: Commands.IConversationPrioritizeOrder,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // copy history and ongoing
    let updated = {
        history: [...state.conversation.history],
        ongoing: [...state.conversation.ongoing],
    };

    // remove ids that need to be removed
    command.remove.forEach((id: number | string): void =>
    {
        updated = removeId(id, updated.history, updated.ongoing);
    });

    // remove ids that will be added to avoid duplicated values
    command.addHistoryBottom.forEach((id: number | string): void =>
    {
        updated = removeId(id, updated.history, updated.ongoing);
    });
    command.addHistoryTop.forEach((id: number | string): void =>
    {
        updated = removeId(id, updated.history, updated.ongoing);
    });
    command.addOngoingBottom.forEach((id: number | string): void =>
    {
        updated = removeId(id, updated.history, updated.ongoing);
    });
    command.addOngoingTop.forEach((id: number | string): void =>
    {
        updated = removeId(id, updated.history, updated.ongoing);
    });

    // add ids
    updated.history = [...command.addHistoryTop,
                       ...updated.history,
                       ...command.addHistoryBottom];
    updated.ongoing = [...command.addOngoingTop,
                       ...updated.ongoing,
                       ...command.addOngoingBottom];

    // generate order prioritized event
    yield events.conversationOrderPrioritized(updated.history, updated.ongoing);
}


/**
 * I am a conversation remove command handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :generates: events
 */
async function* remove (
    command: Commands.IConversationRemove,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get conversation to be removed
    const conversation = state.conversation.byId[command._target];

    // conversation does not exist: stop
    if (conversation === undefined)
    {
        return;
    }

    // conversation exists: remove it from this aggregate and add it to timeline
    await addTimeline(
        conversation.account,
        null,
        customers.getContactByChannelAndUid(
            contactTypesChannelToId.get(conversation.channel),
            conversation.remote,
        ).id,
        conversation.end,
        conversation.id,
        conversation.lastInteraction,
        Object.values(conversation.messages),
        conversation.remote,
        conversation.start,
        null,
        conversation.subject,
    );

    // update history and ongoing
    const updatedState = Store.getState();
    const {history, ongoing} = moveToHistory(conversation.channel,
                                             conversation.remote,
                                             updatedState.conversation.byId,
                                             updatedState.conversation.history,
                                             updatedState.conversation.ongoing);

    // generate conversationRemoved event
    yield events.conversationRemoved(command._target, history, ongoing);
}


/**
 * I am a conversation transfer command handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :generates: events
 */
async function* transfer (command: Commands.IConversationTransfer,
                          state: IAppState): AsyncIterable<IEvent>
{
    // generate transfering event
    yield events.conversationTransferring(command._target,
                                          command.to,
                                          state.conversation.user.token);

    // transfer conversation
    const result = await utils.transfer(command._target,
                                        command.to,
                                        state.user.id);

    // transfer succeeded: generate success event and finish
    if (result.error === null)
    {
        // generate transfered event
        yield events.conversationTransferred(command._target,
                                             command.to,
                                             state.conversation.user.session);
        return;
    }

    // transfer failed: generate failure event
    yield events.conversationNotTransferred(
        command._target,
        command.to,
        state.conversation.user.session,
    );

    // conversation not with user: inform user and remove conversation
    if (result.error === 'conversationExited')
    {
        // show popup
        showUnavailableConversationPopup();

        // reset transfer
        resetTransferConversation();

        // remove conversation
        removeConversation(command._target);
    }
}


/**
 * I am a create command handler.
 *
 * :param command: command
 * :param conversations: conversations
 *
 * :generates: events
 */
async function* create (
    command: Commands.IConversationCreate,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate creating event
    yield events.conversationCreating();

    // get agent
    const agent = fetchUser().id;

    // get contact channel
    const channel =  contactTypesChannelToId.get(
        state.account.byId[command.account]?.channel?.toLowerCase(),
    );

    // get customer
    const customer = customers.getCustomerByContact(channel, command.contact);

    // type is not Email: check for an existing or a sibling conversation
    if (command.type !== 8)
    {
        // there is already a conversation with this contact: abort
        const conversation = selectors.getConversationByAccountAndUid(
            command.account,
            command.contact,
        );

        if (conversation !== null)
        {
            yield events.conversationAlreadyExists(conversation['id']);
            return;
        }

        // get the contact on the store
        const contact = customers.getContactByChannelAndUid(
            channel,
            command.contact,
        );

        // get the siblings loaded for the contact on screen
        const loaded = contact !== null ?
            customers.getSiblings(contact.id) :
            null;

        // get the uids the contact is also known by, unless they are loaded
        const siblings = loaded ??
            await getMappedContactUids(channel, command.contact);

        // get the conversation a sibling holds on the store
        const heldSibling = findSiblingConversationOnStore(
            command.account,
            siblings,
        );

        // a sibling is talking on this store: focus its conversation
        if (heldSibling !== null)
        {
            yield events.conversationAlreadyExists(heldSibling.id);
            return;
        }

        // get the conversation of a sibling on the account, with any agent
        const siblingConversation = await findSiblingConversation(
            command.account,
            siblings,
        );

        // a sibling is in an ongoing conversation: focus it or warn, and abort
        if (siblingConversation !== null)
        {
            // get the sibling conversation on the store
            const stored = selectors.get(siblingConversation.id);

            // the sibling conversation is on the sidebar: focus it
            if (stored !== null &&
                Store.getState().conversation.ongoing.includes(stored.customer)
                === true)
            {
                yield events.conversationAlreadyExists(siblingConversation.id);
                return;
            }

            // warn neutrally
            yield events.conversationWithAnotherApp('');
            return;
        }
    }

    // create conversation
    const result = await createConversation(
        command.account,
        state.user.company,
        customer?.name ?? '',
        command.contact,
        command.subject,
        agent,
    );

    // creation failed: check reason
    if (result.id === null)
    {
        // conversation with another app: generate event and stop
        if (result.reason === 'conversationWithAnotherApp')
        {
            yield events.conversationWithAnotherApp(result.data);
            return;
        }

        // conversation with another agent: generate event and stop
        if (result.reason === 'conversationWithAnotherAgent')
        {
            yield events.conversationWithAnotherAgent(result.data);
            return;
        }

        // other error: generate create failed event and stop
        yield events.conversationNotCreated();
        return;
    }

    // remove from timeline aggregate
    await removeTimeline(result.id);

    // update history and ongoing arrays
    const updatedState = Store.getState();
    const {history, ongoing} = moveToOngoing(customer.id,
                                             updatedState.conversation.history,
                                             updatedState.conversation.ongoing);

    // generate events
    yield events.conversationAdded(command.account,
                                   state.account.byId[command.account].channel,
                                   null,
                                   false,
                                   history,
                                   result.id,
                                   false,
                                   result.lastInteraction,
                                   {},
                                   ongoing,
                                   command.contact,
                                   result.start,
                                   command.subject,
                                   result.templateRequired,
                                   null,
                                   0,
                                   undefined,
                                   result.dynamic);
    yield events.conversationCreated(result.id);
}


/*
 * I am a conversation ensure command handler.
 *
 * :param command: conversation ensure command
 * :param state: application state
 *
 * :returns: events
 */
async function* ensure (
    command: Commands.IConversationEnsure,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get conversation id from command
    const {_target: id} = command;

    // get conversation from state
    const conversation = selectors.getFinished(id);

    // conversation is already in state: return empty list
    if (conversation !== null)
    {
        return [];
    }

    // generate fetching event
    yield events.conversationFetching(id);

    // fetch conversation
    const conversationData = await fetchConversation(state.user.company, id);

    // fetch failed: generate conversation not fetched event and return
    if (conversationData === null)
    {
        yield events.conversationNotFetched(id);
        return;
    }

    // get account from state
    const account = accountsSelectors.get(conversationData.account.id);

    // Generate conversation history data
    yield events.conversationHistoryAdded([{
        account: conversationData.account.id,
        agentFinished: conversationData.agentFinished,
        channel: account?.channel ?? '',
        customer: null,
        id: conversationData.id,
        remote: conversationData.contact.uid,
        start: conversationData.start,
        subject: conversationData.subject,
        unansweredMessages: conversationData.unansweredMessages,
    }]);
}


/*
 * I am a conversation ensure ongoing command handler.
 *
 * :param command: conversation ensure ongoing command
 * :param state: application state
 *
 * :returns: events
 */
async function* ensureOngoing (
    command: Commands.IConversationEnsureOngoing,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get conversation id from command
    const {_target: id} = command;

    // get conversation from state
    const conversation = selectors.get(id);

    // conversation is already in state: return empty list
    if (conversation !== null)
    {
        return [];
    }

    // generate fetching event
    yield events.conversationFetching(id);

    // fetch conversation
    const result = await fetchConversation(state.user.company, id);

    // fetch failed: generate conversation not fetched event and return
    if (result === null)
    {
        yield events.conversationNotFetched(id);
        return;
    }

    // Generate conversation history data
    yield events.conversationAdded(
        result.account.id,
        result.account.channel,
        result.end,
        null,
        [],
        result.id,
        false,
        result.lastInteraction,
        {},
        [],
        result.contact.uid,
        result.start,
        result.subject ?? null,
        false,
        null,
        result.unansweredMessages ?? 0,
        null,
    );
}


/**
 * I am a fetch agents messages metrics command handler.
 *
 * :param command: fetch agents messages metrics command
 * :param state: application state
 *
 * :returns: events
 */
async function* fetchAgentsMessagesMetrics (
    command: Commands.IConversationFetchAgentsMessagesMetrics,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate fetching event
    yield events.conversationAgentsMessagesMetricsFetching();

    // get period
    const {end, start} = selectors.getMetricsPeriod();

    // get metrics filters
    const metrics = (state.conversation as IChatState).metrics;
    const account = command.filters?.account ?? null;
    const queue = command.filters?.queue ?? null;

    // set conversations ids
    let conversations: number[] = metrics.conversations;

    // has account and queue filters: get filtered conversations ids
    if (account !== null && queue !== null)
    {
        conversations = [
            ...(metrics.byAccount[account]?.byQueue?.[queue]
                ?.conversations ?? []),
        ];
    }

    // has account filter: get filtered conversations ids
    else if (account !== null)
    {
        conversations = [...(metrics.byAccount[account]?.conversations ?? [])];
    }

    // has queue filter: get filtered conversations ids
    else if (queue !== null)
    {
        conversations = [...(metrics.byQueue[queue]?.conversations ?? [])];
    }

    // get count of conversations per page
    const perPage = Metadata.config.metrics.pagination.conversations;

    // get agents uids
    const agents = fetchByRole(Role.AGENT).map(agent => agent.uid);

    // get template type id
    const typeId = messageTypesNameToId.get('template');

    // initialize loop variables
    const byAgent: IMessagesMetricsByAgent = {};
    let failed = false;

    // paginate requests
    for (let offset = 0; offset < conversations.length; offset += perPage)
    {
        // fetch messages metrics
        const result = await utils.fetchAgentsMessagesMetrics(
            agents,
            start,
            end,
            typeId,
            conversations.slice(offset, offset + perPage),
        );

        // failed: set as failed and stop loop
        if (result === null)
        {
            failed = true;
            break;
        }

        // update metrics by agent
        Object.entries(result).forEach(([uid, messages]) =>
        {
            byAgent[uid] = (byAgent[uid] || 0) + messages;
        });
    }

    // fetch failed: generate not fetched event and return
    if (failed === true)
    {
        yield events.conversationAgentsMessagesMetricsNotFetched();
        return;
    }

    // generate fetched event
    yield events.conversationAgentsMessagesMetricsFetched(
        byAgent,
        start,
        end,
        command.filters,
    );
}


/**
 * I am a fetch-by-ids command handler.
 *
 * :param command: fetch-by-ids command
 * :param state: application state
 *
 * :returns: events
 */
async function* fetchByIds (
    command: Commands.IConversationFetchByIds,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate fetching event
    yield events.conversationFetchingByIds();

    // get accounts ids
    const accounts = Object.keys(state.account.byId).map(id => Number(id));

    // fetch conversations by ids
    const conversations = await utils.fetchByIds(
        accounts,
        command.conversations,
    );

    // fetch failed: generate not fetched event and return
    if (conversations === null)
    {
        yield events.conversationNotFetchedByIds();
        return;
    }

    // initialize finished conversations list
    const history: IConversationHistory[] = [];

    // for each conversation: push to finished conversations list
    conversations.forEach(conversation =>
    {
        history.push({
            account: conversation.account,
            agentFinished: conversation.agentFinished,
            agentName: utils.getConversationAgentName(conversation),
            channel: state.account.byId[conversation.account]?.channel ?? '',
            end: conversation.end,
            id: conversation.id,
            remote: conversation.contactUid,
            start: conversation.start,
            subject: null,
        });
    });

    // generate event to save conversations on state
    yield events.conversationHistoryAdded(history);

    // generate fetched event
    yield events.conversationFetchedByIds();
}


/**
 * I am a fetch message by original id command handler.
 *
 * :param command: fetch message by original id command
 * :param state: application state
 *
 * :returns: events
 */
async function* fetchMessageByOriginalId (
    command: Commands.IConversationFetchMessageByOriginalId,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get props from command
    const {
        message: messageId,
        _target: conversationId,
        reply,
    } = command;

    // get conversation data
    const conversation = state.conversation.byId[conversationId] ??
                         state.conversation.finishedById[conversationId];

    // conversation does not exist in conversations aggregate: get in timeline
    if (conversation === undefined)
    {
        fetchTimelineMessageByOriginalId(messageId, reply, conversationId);
        return [];
    }

    // get message data
    const message = conversation.messages[messageId];

    // message already fetched: stop
    if (message?.replyFetched === true)
    {
        return [];
    }

    // generate fetching event
    yield events.conversationMessageByOriginalIdFetching(conversation.id,
                                                         messageId);

    // fetch message
    const data = await utils.fetchMessageByOriginalId(reply);

    // fetch failed: generate not fetched event and return
    if (data === null)
    {
        yield events.conversationMessageByOriginalIdNotFetched(conversation.id,
                                                               messageId);
        return;
    }

    // generate fetched event
    yield events.conversationMessageByOriginalIdFetched(conversation.id,
                                                        messageId,
                                                        data);
}


/**
 * I am a fetch metrics command handler.
 *
 * :param command: fetch metrics command
 * :param state: application state
 *
 * :returns: events
 */
async function* fetchMetrics (
    command: Commands.IConversationFetchMetrics,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get periods
    const {end: lastEnd, start: lastStart} = selectors.getLastMetricsPeriod();
    const {end, start} = selectors.getMetricsPeriod();

    // period don't change: stop
    if (lastEnd === end && lastStart === start && command.force !== true)
    {
        return [];
    }

    // generate fetching event
    yield events.conversationMetricsFetching();

    // get accounts ids
    const accounts = Object.keys(state.account.byId).map(id => Number(id));

    // get request period offset
    const days = differenceInDays(end * 1000, start * 1000) || 1;
    const periods = days / 30 * Metadata.config.metrics.pagination.periods;
    const periodOffset = Math.ceil((end - start) / periods);

    // initialize loop variables
    let failed = false;
    const conversations: IConversationsReportMetric[] = [];

    // paginate requests
    for (let offset = start; offset < end; offset += periodOffset)
    {
        // set end offset
        const endOffset = offset + periodOffset;

        // fetch conversations
        const result = await utils.fetchConversationsReport(
            offset,
            endOffset <= end ? endOffset : end,
            accounts,
        );

        // failed: stop
        if (result === null)
        {
            failed = true;
            break;
        }

        // fetched: push to list
        conversations.push(...result);
    }

    // failed: generate not fetched event and stop
    if (failed === true)
    {
        yield events.conversationMetricsNotFetched();
        return;
    }

    // get conversations metrics
    const metrics = utils.reduceConversationsMetrics(
        conversations,
        start,
        end,
    );

    // generate fetched event
    yield events.conversationMetricsFetched(metrics);
}


/*
 * I am a fetch quality report command handler.
 *
 * :param command: quality report command
 * :param state: application state
 *
 * :returns: events
 */
async function* fetchQualityReport (
    command: Commands.IConversationFetchQualityReport,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get quality report attributes
    const {
        analyzing,
        endDate,
        filters,
        loading,
        startDate,
    } = (state.conversation.metrics as IMetrics).quality;

    // already loading, or dispatching an analysis whose answers a reload
    // would orphan: stop
    if (loading === true || analyzing === true)
    {
        return;
    }

    // a reload resets the analysis cycle, as a page refresh would
    qualityUtils.stopQualityAnalysis();

    // generate fetching event
    yield events.conversationQualityReportFetching();

    // fetch metrics: the util catches and logs every failure itself
    const {error, rows} = await utils.getQualityReport(
        startDate,
        endDate,
        filters.account && [filters.account],
        filters.queue,
        filters.agent,
        filters.score && [filters.score],
    );

    // fetch failed: generate not fetched event and return
    if (rows === null)
    {
        yield events.conversationQualityReportNotFetched(error);
        return;
    }

    // reduce each row to what the report shows, and nothing else: a day
    // of conversations is thousands of rows, all kept in memory
    const byId: Record<number, IQualityConversation> = {};
    for (const {analysis, conversation} of rows)
    {
        byId[conversation.id] = {
            account: conversation.account,
            agentName: utils.getConversationAgentName(conversation),
            channel: state.account.byId[conversation.account]?.channel ?? '',
            end: conversation.end ?? null,
            id: conversation.id,
            reason: null,
            remote: conversation.contactUid,
            score: analysis?.score ?? null,
            start: conversation.start,
            status: analysis === null ? 'unanalyzed' : 'analyzed',
        };
    }

    // generate fetched event, one for the whole report
    yield events.conversationQualityReportFetched(byId, endDate, startDate);
}


/*
 * I am a fetch real time metrics command handler.
 *
 * :param command: real time metrics command
 * :param state: application state
 *
 * :returns: events
 */
async function* fetchRealTimeMetrics (
    command: Commands.IConversationFetchRealTimeMetrics,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate fetching event
    yield events.conversationRealTimeMetricsFetching();

    // get real time metrics
    const metrics = await getRealTimeMetrics();

    // fetch failed: generate not fetched event and return
    if (metrics === null)
    {
        yield events.conversationRealTimeMetricsNotFetched();
        return;
    }

    // generate fetched event
    yield events.conversationRealTimeMetricsFetched(metrics);
}


/*
 * I am a conversation fetch command handler.
 *
 * :param command: fetch command
 * :param state: application state
 *
 * :returns: events
 */
async function* fetch (
    command: Commands.IConversationFetch,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // no need to fetch or conversation does not exist: stop
    if ((state.conversation.byId[command._target] === undefined &&
        state.conversation.finishedById[command._target] === undefined) ||
       state.conversation.byId[command._target]?.fetched === true ||
       state.conversation.finishedById[command._target]?.fetched === true ||
       state.conversation.byId[command._target]?.isFetching === true ||
       state.conversation.finishedById[command._target]?.isFetching === true)
    {
        return;
    }

    // messages not fetched or are not being fetched: generate fetching event
    yield events.conversationFetching(command._target);

    // fetch conversation
    const conversation = await fetchMessages(state.user.company,
                                             command._target);

    // fetch failed: generate converstion not fetched event and return
    if (conversation === null)
    {
        yield events.conversationNotFetched(command._target);
        return;
    }

    // initialize has agent answer value
    const hasAgentAnswer = conversation?.hasAgentAnswer ?? null;

    // initialize fetched messages
    const messages : ITimelineMessage[] = [];

    // reduce and fetch messages
    for (const message of conversation.messages)
    {
        // reduce message
        const reduced : ITimelineMessage = await reducePayload(message);

        // prefetch failed: abort
        if (reduced === null)
        {
            yield events.conversationNotFetched(command._target);
            return;
        }

        // add fetched message to list
        messages.push(reduced);
    }

    // fetch succeeded: generate conversation fetched event
    yield events.conversationFetched(command._target, messages, hasAgentAnswer);
}


/**
 * I am a conversation finish command handler.
 *
 * :param command: finish command
 * :param state: application state
 *
 * :generates: events
 */
async function* finish (
    command: Commands.IConversationFinish,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate a conversation finishing event
    yield events.conversationFinishing(command.conversations);

    // get user id
    const user = fetchUser().id;

    // set finished
    const finished = [];

    // map through all conversations that should be finished
    for (const conversation of command.conversations)
    {
        // finish conversation
        const result = await finishConversation(
            user,
            state.user.company,
            conversation,
            state.conversation.user.session,
        );

        // finish succeeded: set conversation as finished
        if (result.error === null)
        {
            finished.push(conversation);
            continue;
        }

        // conversation not with user: inform user and remove conversation
        if (result.error === 'conversationExited')
        {
            // single conversation request: show conversation unavailable popup
            if (command.conversations.length === 1)
            {
                showUnavailableConversationPopup();
            }

            // remove conversation
            removeConversation(conversation);
        }
    }

    // finish failed for at least one: generate conversation not finished event
    if (command.conversations.length > finished.length)
    {
        yield events.conversationNotFinished();
    }

    // finish successful for all conversations: generate success event
    else
    {
        yield events.conversationFinished(finished);
    }
}


/**
 * I am a conversation focus command handler.
 *
 * :param command: focus command
 * :param state: application state
 *
 * :generates: events
 */
async function* focus (
    command: Commands.IConversationFocus,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get command props
    const {account, contact, conversation} = command;

    // make current channel id
    const channel = `${contact}_${account}`;

    // focus conversation
    yield events.conversationFocused(conversation, account, channel, contact);
}


/**
 * I am a conversation force finish command handler.
 *
 * :param command: force finish command
 * :param state: application state
 *
 * :generates: events
 */
async function* forceFinish (
    command: Commands.IConversationForceFinish,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate a conversation finishing event
    yield events.conversationFinishing([command._target]);

    // force finish conversation
    const result = await forceFinishConversation(command._target);

    // finish failed: generate not finished event and return
    if (result === null)
    {
        yield events.conversationNotFinished();
        return;
    }

    // generate finished event
    yield events.conversationFinished([command._target]);
}


/**
 * I am a load online agents handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :generates: events
 */
async function* loadOnlineAgents (
    command: Commands.IConversationLoadOnlineAgents,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate online agents loading event
    yield events.conversationOnlineAgentsLoading();

    // load agents
    const agents = await getOnlineAgents(command.company);

    // agents load failed: generate online agents not loaded event and stop
    if (agents === null)
    {
        yield events.conversationOnlineAgentsNotLoaded();
        return;
    }

    // generate online agents loaded event
    yield events.conversationOnlineAgentsLoaded();

    // generate online agents loaded event for each agent
    for (const agent of agents)
    {
        yield events.conversationOnlineAgentAdded(agent);
    }
}


/**
 * I am an email send command handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :generates: events
 */
async function* sendEmail (
    command: Commands.IConversationSendEmail,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get conversation id
    let id = command._target;

    // get agent uid
    const agent = fetchUser().id;

    // get contact uid
    const contact = command.contact;

    // get timestamp
    const timestamp = Date.now();

    // get original Id
    const originalId = id ? `${id}.${timestamp}` : uuid4();

    // initialize message
    const message = {
        attachments: [],
        body: command.body,
        originalId,
        outgoing: command.sender,
        timestamp,
        type: 'email',
    };

    // get state ongoing
    let ongoing = state.conversation.ongoing;

    // conversation not passed: create one
    if (id === null)
    {
        // get customer
        const customer = customers.getCustomerByContact(
            contactTypesChannelToId.get(
                state.account.byId[command.account].channel.toLowerCase(),
            ),
            command.contact,
        );

        // generate creating event
        yield events.conversationCreating();

        // create conversation
        const result = await createConversation(
            command.account,
            state.user.company,
            customer?.name ?? '',
            command.contact,
            command.subject,
            agent,
        );

        // creation failed: check reason
        if (result.id === null)
        {
            // conversation with another app: generate event and stop
            if (result.reason === 'conversationWithAnotherApp')
            {
                yield events.conversationWithAnotherApp(result.data);
                return;
            }

            // conversation with another agent: generate event and stop
            if (result.reason === 'conversationWithAnotherAgent')
            {
                yield events.conversationWithAnotherAgent(result.data);
                return;
            }

            // other error: generate create failed event and stop
            yield events.conversationNotCreated();
            return;
        }

        // remove from timeline aggregate and generate events
        id = result.id;
        await removeTimeline(id);

        // update history and ongoing arrays
        const updatedState = Store.getState();
        const {history, ongoing: ongoingAdded} = moveToOngoing(
            customer.id,
            updatedState.conversation.history,
            updatedState.conversation.ongoing,
        );

        // generate events
        yield events.conversationAdded(
            command.account,
            state.account.byId[command.account].channel,
            null,
            true,
            history,
            id,
            false,
            result.lastInteraction,
            {},
            ongoingAdded,
            command.contact,
            result.start,
            command.subject,
            false,
            null,
            0,
            undefined,
        );
        yield events.conversationCreated(id);

        // initialize conversation
        let conversation = null;

        // check if conversation started correctly
        for (let i = 0; i < TRIES; i++)
        {
            // get conversation app
            conversation = await utils.fetchApp(state.user.company, id);

            // conversation started correctly: stop checking
            if (conversation !== null ||
                conversation?.application?.type === 'agent' ||
                conversation?.application?.uid === agent)
            {
                break;
            }

            // wait
            await utils.sleep(DURATION);
        }

        // conversation not started: fail
        if (conversation === null ||
            conversation.application === null ||
            conversation.application.type !== 'agent' ||
            conversation.application.uid !== agent)
        {
            yield events.conversationMessageNotSent(id, message);
            return;
        }

        // conversation started: get the most recent ongoing
        ongoing = Store.getState().conversation.ongoing;
    }

    // conversation passed: update ongoing array
    else
    {
        ongoing = moveToTop(
            state.account.byId[command.account].channel.toLowerCase(),
            command.contact,
            state.conversation.ongoing,
        );
    }

    // generate message sending event
    yield events.conversationMessageSending(id, message, ongoing);

    // upload attachments and add them to the message
    let uploadId: string;
    for (const attachment of command.attachments)
    {
        // upload email to file server
        try
        {
            uploadId = await uploadFile(
                attachment,
                command.onProgress.bind(null, id, originalId),
                command.onSuccess.bind(null, id, originalId),
            );
        }

        // upload failed: log, generate failure events and stop
        catch (error)
        {
            console.error(error);
            yield events.conversationFileNotUploaded(id, message.originalId);
            yield events.conversationMessageNotSent(id, message);
            return;
        }

        // upload succeeded: add attachment to message
        message.attachments.push({
            id: uploadId,
            mime: attachment.type,
            name: attachment.name,
            thumbnail: null,
        });
    }

    // upload email
    try
    {
        uploadId = await uploadEmail(
            command.body,
            message.attachments,
            `${originalId}.json`,
            command.onProgress.bind(null, id, originalId),
            command.onSuccess.bind(null, id, originalId),
        );
    }

    // upload failed: log, generate failure events and return
    catch (error)
    {
        console.error(error);
        yield events.conversationFileNotUploaded(id, message.originalId);
        yield events.conversationMessageNotSent(id, message);
        return;
    }

    // upload succeeded: send message
    const result = await send(
        uploadId,
        state.user.company,
        contact,
        id,
        originalId,
        agent,
        messageTypesNameToId.get('email'),
    );

    // send message succeeded: set message, generate event and finish
    if (result.id !== null)
    {
        message['id'] = result.id;
        yield events.conversationMessageSent(id, message, originalId);
        return;
    }

    // message unsupported: generate specific failure event and finish
    if (result.reason === 'unsupported')
    {
        yield events.conversationUnsupportedMessageSent(message.body,
                                                        id,
                                                        originalId);
        return;
    }

    // send failed: generate failure event
    yield events.conversationMessageNotSent(id, message);

    // conversation not with user: inform user and remove conversation
    if (result.reason === 'conversationExited')
    {
        // show popup
        showUnavailableConversationPopup();

        // remove conversation
        removeConversation(id);
    }
}


/**
 * I am a message send command handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :generates: events
 */
async function* sendMessage (
    command: Commands.IConversationSendMessage,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // initialize conversation id
    const id = command._target;

    // get agent uid
    const agent = fetchUser().id;

    // get contact uid
    const contact = command.contact;

    // initialize message
    const message: IMessage = {...command.message};

    // convert message type id to name
    const type = messageTypesIdToName.get(message.type as number);

    // update ongoing array
    const account = state.conversation.byId[command._target].account;
    const ongoing = moveToTop(
        state.account.byId[account].channel.toLowerCase(),
        command.contact,
        state.conversation.ongoing,
    );

    // generate message sending event
    yield events.conversationMessageSending(id, {...message, type}, ongoing);

    // non-text payload media not uploaded yet: upload it to file server
    if (message.type !== 0 && command.uploaded !== true)
    {
        // upload file to file server
        try
        {
            message.body = await uploadFile(
                message.file,
                command.onProgress.bind(null, id, message.originalId),
                command.onSuccess.bind(null, id, message.originalId),
            );
        }

        // upload failed: log, generate failure events and stop
        catch (error)
        {
            console.error(error);
            yield events.conversationFileNotUploaded(id, message.originalId);
            yield events.conversationMessageNotSent(id, message);
            return;
        }
    }

    // send message
    const result = await send(
        message.body,
        state.user.company,
        contact,
        id,
        message.originalId,
        agent,
        message.type as number,
        message.caption,
        message.reply,
    );

    // send message succeeded: set message id, generate event and finish
    if (result.id !== null)
    {
        yield events.conversationMessageSent(
            id,
            {...message, id: result.id, originalId: result.originalId},
            message.originalId,
        );
        return;
    }

    // message unsupported: generate specific event and finish
    if (result.reason === 'unsupported')
    {
        yield events.conversationUnsupportedMessageSent(message.body,
                                                        id,
                                                        message.originalId);
        return;
    }

    // send failed: generate event
    yield events.conversationMessageNotSent(id, message);

    // conversation not with user: inform user and remove
    if (result.reason === 'conversationExited')
    {
        // show popup
        showUnavailableConversationPopup();

        // remove conversation
        removeConversation(id);
    }
}


/**
 * I am a conversation send template command handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :generates: events
 */
async function* sendTemplate(
    command: Commands.IConversationSendTemplate,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get conversation id
    const {_target: conversationId, templateInput} = command;

    // generate original message id
    const originalId = conversationId ?
        `${conversationId}.${Date.now()}.${uuid4()}` : uuid4();

    // create body content from template input
    const templateBody = {
        fields: {},
        id: templateInput?.id ?? '',
        language: templateInput?.language ?? '',
        name: templateInput?.name ?? '',
        params: templateInput?.params ?? [],
        text: templateInput?.data,
    };

    // create message with template information
    const template: IMessage = {
        attachments: [],
        body: JSON.stringify(templateBody),
        originalId: originalId,
        outgoing: fetchUser().id,
        timestamp: Date.now(),
        type: messageTypesNameToId.get('template'),
    };

    // generate template sending event
    yield events.conversationTemplateSending(conversationId, template);

    // get logged agent
    const agent = fetchUser().id;

    // send template
    const {message, reason} = await utils.sendTemplate(
        state.user.company,
        conversationId,
        agent,
        template.originalId,
        templateInput,
    );

    // send message succeeded: set message id, generate event and finish
    if (message !== undefined && message.id !== null)
    {
        yield events.conversationTemplateSent(
            conversationId,
            template.originalId,
            message,
        );
        return;
    }

    // send failed: generate event
    yield events.conversationTemplateNotSent(
        conversationId,
        template.originalId,
    );

    // conversation not with user: inform user and remove
    if (reason === 'conversationExited')
    {
        // show popup
        showUnavailableConversationPopup();

        // remove conversation
        removeConversation(conversationId);
    }
}


/**
 * I am a conversation created command handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :generates: events
 */
async function* onCreate (command: Commands.IConversationOnCreate,
                          state: IAppState): AsyncIterable<IEvent>
{
    // get command input
    const {_target: id} = command;
    const {account} = command;
    const {channel} = command;
    const {contactUid} = command;
    const {hasAgentAnswer} = command;
    const {lastInteraction} = command;
    const {name} = command;
    const {start} = command;
    const {subject} = command;
    const {templateRequired} = command;
    const {templateSent} = command;
    const {unansweredMessages} = command;

    // add customer to store and get its id
    const customerId = await ensureCustomer(
        channel,
        name,
        contactUid,
        true,
    );

    // get existing messages from conversation
    const messages =
    {
        ...(selectors.get(id)?.messages ||
        getTimeline(id)?.messages),
    };

    // remove conversation from timeline aggregate
    await removeTimeline(id);

    // channel is not email: remove previous conversations
    if (channel !== 'email')
    {
        await removePreviousConversations(account, contactUid, id);
    }

    // update history and ongoing arrays
    const updatedState = Store.getState();
    const {history, ongoing} = moveToOngoing(customerId,
                                             updatedState.conversation.history,
                                             updatedState.conversation.ongoing);

    // add conversation to conversations aggregate
    yield events.conversationAdded(account,
                                   channel,
                                   null,
                                   hasAgentAnswer,
                                   history,
                                   id,
                                   false,
                                   lastInteraction,
                                   messages,
                                   ongoing,
                                   contactUid,
                                   start,
                                   subject,
                                   templateRequired,
                                   templateSent,
                                   unansweredMessages,
                                   undefined);

    // need to focus received conversation: focus it
    const conversationContact = customers.getContactByChannelAndUid(
        contactTypesChannelToId.get(channel),
        contactUid,
    );

    // get updated conversation props
    const {currentAccount, currentContact} = Store.getState().conversation;

    // agent is on screen: reensure its focus
    if (conversationContact !== null &&
        conversationContact.id === currentContact &&
        currentAccount === account &&
        (channel !== 'email' ||
        (channel === 'email' && id === state.timeline.current)))
    {
        await focusAPI(currentAccount, currentContact, id);
    }
}


/**
 * I am an echo conversation create command handler.
 *
 * :param command: echo create command
 * :param state: application state
 *
 * :returns: events
 */
async function* onEchoCreate (command: Commands.IConversationOnEchoCreate,
                              state: IAppState): AsyncIterable<IEvent>
{
    // get command input
    const {_target: id} = command;
    const {account} = command;
    const {channel} = command;
    const {contactUid} = command;
    const {hasAgentAnswer} = command;
    const {lastInteraction} = command;
    const {name} = command;
    const {start} = command;
    const {subject} = command;
    const {templateRequired} = command;
    const {templateSent} = command;
    const {unansweredMessages} = command;

    // add customer to store and get its id
    const customerId = await ensureCustomer(
        channel,
        name,
        contactUid,
        true,
    );

    // get existing messages from conversation
    const messages =
    {
        ...(selectors.get(id)?.messages ||
        getTimeline(id)?.messages),
    };

    // remove conversation from timeline aggregate
    await removeTimeline(id);

    // channel is not email: remove previous conversations
    if (channel !== 'email')
    {
        await removePreviousConversations(account, contactUid, id);
    }

    // update history and ongoing arrays
    const updatedState = Store.getState();
    const {history, ongoing} = moveToOngoing(customerId,
                                             updatedState.conversation.history,
                                             updatedState.conversation.ongoing);

    // add conversation to conversations aggregate without changing agent focus
    yield events.conversationAdded(account,
                                   channel,
                                   null,
                                   hasAgentAnswer,
                                   history,
                                   id,
                                   false,
                                   lastInteraction,
                                   messages,
                                   ongoing,
                                   contactUid,
                                   start,
                                   subject,
                                   templateRequired,
                                   templateSent,
                                   unansweredMessages,
                                   undefined,
                                   {direction: 'echo'});

    // get conversation contact
    const contact = customers.getContactByChannelAndUid(
        contactTypesChannelToId.get(channel),
        contactUid,
    );

    // agent is on screen: reensure its focus
    if (contact !== null &&
        contact.id === state.conversation.currentContact &&
        state.conversation.currentAccount === account &&
        (channel !== 'email' ||
        (channel === 'email' && id === state.timeline.current)))
    {
        await focusAPI(
            state.conversation.currentAccount,
            state.conversation.currentContact,
            id,
        );
    }
}


/**
 * I am an on echo message command handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :returns: events
 */
async function* onEchoMessage (
    command: Commands.IConversationOnEchoMessage,
    state: IAppState): AsyncIterable<IEvent>
{
    // get conversation id
    const {_target: conversation} = command;

    // conversation doesn't exists: stop
    const stored = state.conversation.byId[conversation] ?? null;
    if (stored === null)
    {
        return;
    }

    // reduce message
    const name = messageTypesIdToName.get(command.messageType);
    const message = await reducePayload({
        ack: command.ack,
        body: command.body,
        caption: command.caption,
        id: command.id,
        mode: command.mode,
        name: command.name,
        originalId: command.originalId,
        outgoing: command.outgoing,
        referral: command.referral,
        reply: command.reply,
        timestamp: command.timestamp,
        type: name,
    });

    // message not found: stop
    if (message === null)
    {
        return;
    }

    // update ongoing array
    const updatedState = Store.getState();
    const ongoing = moveToTop(
        state.account.byId[stored.account].channel.toLowerCase(),
        stored.remote,
        updatedState.conversation.ongoing,
    );

    // generate message received event
    yield events.conversationMessageReceived(
        message.ack,
        message.attachments,
        message.body,
        message.caption,
        conversation,
        message.deleted,
        message.id,
        command.messageClass,
        name,
        message.mode,
        message.type,
        ongoing,
        message.originalId,
        message.reducedBody,
        message.referral,
        message.reply,
        message.timestamp,
        command.uid,
        command.unansweredMessages,
        command.outgoing,
    );
}


/**
 * I am an on feedback command handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :generates: events
 */
async function* onFeedback (command: Commands.IConversationOnFeedback,
                            state: IAppState): AsyncIterable<IEvent>
{
    // get feedback message
    const message = getMessage(command.code, command.params);

    // feedback is not present: generate feedback added event
    if (find(state.conversation.feedbacks, {message}) === undefined)
    {
        yield events.conversationFeedbackAdded(command.closable,
                                               command.duration,
                                               message,
                                               command.title,
                                               command.type);
    }
}


/**
 * I am on heartbeat conflict command handler.
 *
 * :param command: on heartbeat conflict command
 * :param state: application state
 *
 * :generates: events
 */
async function* onHeartbeatConflict (
    command: Commands.IConversationOnHeartbeatConflict,
    state: IAppState,
): AsyncIterable<IEvent>
{
    yield events.conversationSessionForciblyEnded();
}


/**
 * I am an on heartbeat success command handler.
 *
 * :param command: on heartbeat success command
 * :param state: application state
 *
 * :generates: events
 */
async function* onHeartbeatSuccess (
    command: Commands.IConversationOnHeartbeatSuccess,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // emit event
    yield events.conversationHeartbeatSucceeded(command.conversationsOnQueue);

    // last heartbeat failed: generate session opened event
    if (state.conversation.connected !== true)
    {
        yield events.conversationSessionOpened(
            state.conversation.user.push ?? null,
            state.conversation.user.session,
        );
    }
}


/**
 * I am an on heartbeat failure command handler.
 *
 * :param command: on heartbeat failure command
 * :param state: application state
 *
 * :generates: events
 */
async function* onHeartbeatFailure (
    command: Commands.IConversationOnHeartbeatFailure,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // log failure
    logger.info('CVJPZ0060I', {
        company: state.user.company,
        name: state.user.name,
        uid: state.user.id,
    });

    // last heartbeat succeeded: generate session closed event
    if (state.conversation.connected !== false &&
        state.conversation.connecting === 0)
    {
        yield events.conversationSessionClosed();
    }

    // clear timeline by conversation
    await clearByConversation();

    // reopen session
    const result = await session.open(state.user.id,
                                      state.user.company,
                                      state.user.name);

    // reopen session failed: generate event, schedule new attempt, log, abort
    if (result === null)
    {
        // generate failure event
        yield events.conversationSessionNotOpened();

        // schedule new attempt and update counter
        setTimeout(() => onHeartbeatFailureAPI(), 5000 * (2 * count + 1));
        count += 1;

        // log failure
        logger.error('CVJPZ0062E', {
            company: state.user.company,
            name: state.user.name,
            uid: state.user.id,
        });

        // abort
        return;
    }

    // reset reconnections counter
    count = 0;

    // reopen session succeeded: generate login succeeded event
    yield events.conversationSessionOpened(
        result.push,
        result.session,
    );
}


/**
 * I am an on message command handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :returns: events
 */
async function* onMessage (command: Commands.IConversationOnMessage,
                           state: any): AsyncIterable<IEvent>
{
    // get conversation id
    const {_target: conversation} = command;

    // get conversations
    const {byId} = state.conversation;

    // conversation doesn't exists: return
    if (byId[conversation] === undefined)
    {
        return;
    }

    // conversation exists: convert message type id to name
    const name = messageTypesIdToName.get(command.messageType);

    // reduce message
    const message = await reducePayload({
        ack: command.ack,
        body: command.body,
        caption: command.caption,
        id: command.id,
        mode: command.mode,
        name: command.name,
        originalId: command.originalId,
        outgoing: false,
        referral: command.referral,
        reply: command.reply,
        timestamp: command.timestamp,
        type: name,
    });

    // message not found: stop
    if (message === null)
    {
        return;
    }

    // update ongoing array
    const updatedState = Store.getState();
    const ongoing = moveToTop(
        state.account.byId[byId[conversation].account].channel.toLowerCase(),
        byId[conversation].remote,
        updatedState.conversation.ongoing,
    );

    // generate message received event
    yield events.conversationMessageReceived(message.ack,
                                             message.attachments,
                                             message.body,
                                             message.caption,
                                             conversation,
                                             message.deleted,
                                             message.id,
                                             command.messageClass,
                                             name,
                                             message.mode,
                                             message.type,
                                             ongoing,
                                             message.originalId,
                                             message.reducedBody,
                                             message.referral,
                                             message.reply,
                                             message.timestamp,
                                             command.uid,
                                             command.unansweredMessages);

    // get channel
    const channel = byId[conversation].channel;

    // channel is email: no need for auto answer
    if (channel === MessageTypes.EMAIL)
    {
        return;
    }

    // send specified message
    const answered = await sendAutoAnswer(command.uid, conversation);

    // auto message was sent: don't send it again
    if (answered === true)
    {
        yield events.conversationAutoAnswered(conversation);
    }
}


/**
 * I am an on type text command handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :generates: events
 */
async function* onTypeText (
    command: Commands.IConversationOnTypeText,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get conversation
    const conversation = state.conversation.byId[command._target];

    // conversation does not exist: stop function
    if (conversation === undefined)
    {
        return;
    }

    yield events.conversationTypedText(
        command._target,
        command.text,
    );
}


/**
 * I am an on message ack command handler.
 *
 * :param command: on message ack command
 * :param state: application state
 *
 * :generates: events
 */
async function* onMessageAck(
    command: Commands.IConversationOnMessageAck,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // conversation or message does not exist: return
    if (selectors.getMessageById(command._target, command.id) === null)
    {
        return;
    }

    // command has originalId: yield message original id received event
    if (command.originalId !== null)
    {
        yield events.conversationMessageOriginalIdReceived(command._target,
                                                           command.id,
                                                           command.originalId);
    }

    // yield conversation message ack received event
    yield events.conversationMessageAckReceived(command._target,
                                                command.id,
                                                command.ack);
}


/**
 * I am an on progress upload command handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :generates: events
 */
async function* onUploadProgress (command: IConversationOnUploadProgress,
                                  state: IAppState): AsyncIterable<IEvent>
{
    yield events.conversationFileUploading(command.id,
                                           command.originalId,
                                           command.percentage);
}


/**
 * I am an on success upload command handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :generates: events
 */
async function* onUploadSuccess (command: IConversationOnUploadSuccess,
                                 state: IAppState): AsyncIterable<IEvent>
{
    yield events.conversationFileUploaded(command.id, command.originalId);
}


/**
 * I am an open session command handler.
 *
 * :param command: conversation open session command
 * :param state: application state
 *
 * :generates: events
 */
async function* openSession (
    command: Commands.IConversationOpenSession,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // open session
    const result = await session.open(state.user.id,
                                      state.user.company,
                                      state.user.name);

    // open session failed: generate event and return
    if (result === null)
    {
        yield events.conversationSessionNotOpened();
        return;
    }

    // login succeeded: generate login succeeded event
    yield events.conversationSessionOpened(
        result.push,
        result.session,
    );
}


/**
 * I am a remove feedback command handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :generates: events
 */
async function* removeFeedback (command: Commands.IConversationRemoveFeedback,
                                state: IAppState): AsyncIterable<IEvent>
{
    yield events.conversationFeedbackRemoved(command.index);
}


/**
 * I am a conversation remove forwarding message command handler.
 *
 * :param command: conversation remove forwarding message command
 * :param state: application state
 *
 * :generates: events
 */
async function* removeForwardingMessage (
    command: Commands.IConversationRemoveForwardingMessage,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate forwarding message removed event
    yield events.conversationForwardingMessageRemoved(command.message);
}


/**
 * I am a reset command handler.
 *
 * :generates: events
 */
async function* resetError (command: Commands.IConversationResetError,
                            state: IAppState): AsyncIterable<IEvent>
{
    yield events.conversationErrorReset();
}


/** I am a conversation transfer reset.
 *
 * :param command: command
 * :param state: application state
 *
 * :generates: events
 */
async function* resetTransfer (command: Commands.IConversationResetTransfer,
                               state: IAppState): AsyncIterable<IEvent>
{
    yield events.conversationTransferReset();
}


/**
 * I am a retry message command handler.
 *
 * :param command: command
 * :param state: application state
 *
 * :returns: events
 */
async function* retryMessage (command: Commands.IConversationRetryMessage,
                              state: IAppState): AsyncIterable<IEvent>
{
    // get conversation id
    const {_target: id} = command;

    // set timestamp
    const timestamp = Date.now();

    // set message
    const message: IMessage = {
        ...command.message,
        timestamp,
    };

    // generate message retrying event
    yield events.conversationMessageRetrying(id, message);

    // conversation does not exist: abort
    if (state.conversation.byId[command._target] === undefined)
    {
        return;
    }

    // retry message
    const result = await retry(state.user.company,
                               command._target,
                               command.message.id);

    // retry message succeeded: generate event and finish
    if (result.id !== null)
    {
        yield events.conversationMessageSent(
            command._target,
            message,
            message.originalId,
        );
        return;
    }

    // retry message failed: generate conversation message not sent
    yield events.conversationMessageNotSent(command._target, message);

    // conversation not with user: inform user and remove conversation
    if (result.error === 'conversationExited')
    {
        // show popup
        showUnavailableConversationPopup();

        // remove conversation
        removeConversation(id);
    }
}


/**
 * I am a set metrics period command handler.
 *
 * :generates: events
 */
async function* setMetricsPeriod (
    command: Commands.IConversationSetMetricsPeriod,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get command props
    const {endDate, filters, startDate} = command;

    // generate metrics period set event
    yield events.conversationMetricsPeriodSet(endDate, startDate, filters);
}


/**
 * I am an analyze quality command handler.
 *
 * :param command: analyze quality command
 * :param state: application state
 *
 * :generates: events
 */
async function* analyzeQuality (
    command: Commands.IConversationAnalyzeQuality,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get quality report attributes
    const {analyzing, byId, loading} = (
        state.conversation.metrics as IMetrics
    ).quality;

    // already analyzing or loading: stop
    if (analyzing === true || loading === true)
    {
        return;
    }

    // get candidates
    const conversations = Object.values(byId)
        .filter(qualityUtils.isAnalyzable)
        .map(conversation => conversation.id);

    // nothing to analyze: stop
    if (conversations.length === 0)
    {
        return;
    }

    // generate started event
    yield events.conversationQualityAnalysisStarted();

    // open the socket before dispatching anything. Subscribing does not
    // throw for a closed socket; what can throw is reading the session or
    // the client. Either way the cycle ends here, or the button stays stuck
    // on analyzing
    try
    {
        qualityUtils.subscribeConversationAnalyzed();
    }
    catch (exception)
    {
        // log what failed, as the screen only says the service is unavailable
        const {company, id: user, name} = fetchUser();
        logger.error(
            'CVJPZ0100E',
            {
                company,
                name,
                reason: exception instanceof Error ?
                    exception.message : String(exception),
                topic: CONVERSATION_ANALYZED.getTopic(),
                user,
            },
        );

        yield events.conversationQualityAnalysisFailed('0');
        yield events.conversationQualityAnalysisFinished();
        return;
    }

    // dispatch in batches
    let pending = 0;
    for (const batch of qualityUtils.chunk(
        conversations,
        qualityUtils.MAX_DISPATCH,
    ))
    {
        // dispatch batch
        const result = await utils.analyzeConversations(batch);

        // request failed: report once and stop, the next click retries
        if (result.error !== null ||
            result.dispatched === undefined ||
            result.alreadyAnalyzed === undefined ||
            result.rejected === undefined)
        {
            yield events.conversationQualityAnalysisFailed(
                result.error ?? '0',
            );
            break;
        }

        // generate dispatched event
        yield events.conversationQualityAnalysisDispatched(
            result.alreadyAnalyzed,
            result.dispatched,
            result.rejected,
        );

        // dispatched conversations: schedule their expiration
        if (result.dispatched.length > 0)
        {
            pending += result.dispatched.length;
            qualityUtils.scheduleExpiration(result.dispatched);
        }
    }

    // generate finished event
    yield events.conversationQualityAnalysisFinished();

    // nothing is pending: nothing to wait for
    if (pending === 0)
    {
        qualityUtils.stopQualityAnalysis();
    }
}


/**
 * I am an expire quality analyses command handler.
 *
 * :param command: expire quality analyses command
 * :param state: application state
 *
 * :generates: events
 */
async function* expireQualityAnalyses (
    command: Commands.IConversationExpireQualityAnalyses,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get quality report attributes
    const {analyzing, byId} = (state.conversation.metrics as IMetrics).quality;

    // get conversations still pending
    const stale = command.conversations
        .filter(id => byId[id]?.status === 'pending');

    // nothing pending among them: stop
    if (stale.length === 0)
    {
        return;
    }

    // generate expired event
    yield events.conversationQualityAnalysesExpired(stale);

    // count what remains pending
    const remaining = Object.values(byId)
        .filter(conversation => (
            conversation.status === 'pending' &&
            stale.includes(conversation.id) === false
        ))
        .length;

    // nothing remains and no dispatch in flight: release the socket
    if (remaining === 0 && analyzing === false)
    {
        qualityUtils.stopQualityAnalysis();
    }
}


/**
 * I am an on quality analyzed command handler.
 *
 * :param command: on quality analyzed command
 * :param state: application state
 *
 * :generates: events
 */
async function* onQualityAnalyzed (
    command: Commands.IConversationOnQualityAnalyzed,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get quality report attributes
    const {analyzing, byId} = (state.conversation.metrics as IMetrics).quality;

    // not one of ours (a stale one still is, its event just came late): discard
    const status = byId[command.conversation]?.status;
    if (status !== 'pending' && status !== 'stale')
    {
        return;
    }

    // failed: no score. Analyzed: the score. The reducer copies what it gets
    yield events.conversationQualityAnalyzed(
        command.conversation,
        command.failed === true ? null : command.score,
        command.failed === true ? 'failed' : 'analyzed',
    );

    // count what remains pending
    const remaining = Object.values(byId)
        .filter(conversation => (
            conversation.status === 'pending' &&
            conversation.id !== command.conversation
        ))
        .length;

    // nothing remains and no dispatch in flight: release the socket
    if (remaining === 0 && analyzing === false)
    {
        qualityUtils.stopQualityAnalysis();
    }
}


/**
 * I am a stop quality report command handler.
 *
 * :param command: stop quality report command
 * :param state: application state
 *
 * :generates: events
 */
async function* stopQualityReport (
    command: Commands.IConversationStopQualityReport,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // release the socket and the timers
    qualityUtils.stopQualityAnalysis();

    // generate stopped event
    yield events.conversationQualityReportStopped();
}


/**
 * I am a set quality report metadata command handler.
 *
 * :generates: events
 */
async function* setQualityMetadata (
    command: Commands.IConversationSetQualityMetadata,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate quality report metadata set event
    yield events.conversationQualityMetadataSet(command.metadata);
}


/**
 * I am a start command handler.
 *
 * :generates: events
 */
async function* start (command: Commands.IConversationStart,
                       state: IAppState): AsyncIterable<IEvent>
{
    // user is an administrator: start conversations, responders and session
    if (command.isAdmin === false)
    {
        // start conversations
        startConversations();

        // call api open session in conversis
        await openSessionAPI();
    }

    // generate start event
    yield events.conversationStarted();
}


/**
 * I am a conversation start forward command handler.
 *
 * :param command: conversation start forward command
 * :param state: application state
 *
 * :generates: events
 */
async function* startForward (
    command: Commands.IConversationStartForward,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate forward started event
    yield events.conversationForwardStarted();
}


/**
 * I am a stop command handler.
 *
 * :generates: events
 */
async function* stop (command: Commands.IConversationStop,
                      state: IAppState): AsyncIterable<IEvent>
{
    // user is not administrator: stop responders and close session
    if (command.isAdmin === false)
    {
        responders.stop();
        await closeSessionAPI();
    }

    // generate stop event
    yield events.conversationStopped();
}


/**
 * I am a conversation stop forward command handler.
 *
 * :param command: conversation stop forward command
 * :param state: application state
 *
 * :generates: events
 */
async function* stopForward (
    command: Commands.IConversationStopForward,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate forward stopped event
    yield events.conversationForwardStopped();
}


/**
 * EXPORTS
 */
export {
    addForwardingMessage,
    analyzeQuality,
    closeSession,
    create,
    ensure,
    ensureOngoing,
    expireQualityAnalyses,
    fetch,
    fetchAgentsMessagesMetrics,
    fetchByIds,
    fetchMessageByOriginalId,
    fetchMetrics,
    fetchQualityReport,
    fetchRealTimeMetrics,
    finish,
    focus,
    forceFinish,
    listen,
    load,
    loadByCustomer,
    loadByPeriod,
    loadOngoingByCustomer,
    loadOnlineAgents,
    loadPage,
    onQualityAnalyzed,
    prioritizeOrder,
    onCreate,
    onEchoCreate,
    onEchoMessage,
    onExit,
    onFeedback,
    onHeartbeatConflict,
    onHeartbeatFailure,
    onHeartbeatSuccess,
    onMessage,
    onTypeText,
    onMessageAck,
    onUploadProgress,
    onUploadSuccess,
    openSession,
    remove,
    removeFeedback,
    removeForwardingMessage,
    resetError,
    resetTransfer,
    retryMessage,
    sendEmail,
    sendMessage,
    sendTemplate,
    setMetricsPeriod,
    setQualityMetadata,
    start,
    startForward,
    stop,
    stopForward,
    stopQualityReport,
    transfer,
};
